import type { JSX, ParentComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import "./TextMono.css";

export type TextColor =
	| "white"
	| "fg1"
	| "fg2"
	| "fg3"
	| "fg4"
	| "primary"
	| "error"
	| "warning"
	| "success"
	| "blue"
	| "purple";

export type TextSize = "display" | "title" | "subtitle" | "heading" | "body";

export type TextMonoWeight = "regular" | "bold";

/** Maps to CSS `text-transform` */
export type TextTransform = "none" | "capitalize" | "uppercase" | "lowercase";

type Props = {
	as?: "div" | "span" | "p";
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	title?: string;
	/**
	 * Maps to `--font-size-*` in shared-core.css.
	 * @default "standard"
	 * Rem values are wide (>500px) / narrow (≤500px) viewport.
	 * - display: 2.5rem / 2.5rem
	 * - title: 1.25rem / 1.0625rem
	 * - subtitle: 1rem / 1rem
	 * - heading: 0.875rem / 0.9375rem
	 * - body: 0.75rem / 0.75rem
	 */
	size?: TextSize;

	weight?: TextMonoWeight;

	/**
	 * Maps to `--colour-fg-*`, `--white`, and status tokens in shared-core.css.
	 * Light / dark pairs use `light-dark()`; brand status colours are fixed.
	 * - white: `--white` — `--source-white` #fefdff / `--source-black` #050505
	 * - fg1: `--colour-fg-1` — `--source-grey-800` #4a5158 / `--source-grey-200` #e9ecef
	 * - fg2: `--colour-fg-2` — `--source-grey-700` #646c74 / `--source-grey-400` #ced4da
	 * - fg3: `--colour-fg-3` — `--source-grey-600` #818991 / `--source-grey-500` #9fa7af
	 * - fg4: `--colour-fg-4` — `--source-grey-500` #9fa7af / `--source-grey-700` #646c74
	 * - primary: `--gvt-primary` — `--gvt-blue` #0f6bfe (fixed)
	 * - error: `--gvt-error` — `--gvt-error` #de0d2c (fixed)
	 * - warning: `--gvt-warning` — `--gvt-warning` #de7c0d (fixed)
	 * - success: `--gvt-green` — `--gvt-green` #0cb25f (fixed)
	 * - blue: `--gvt-blue` — `--gvt-blue` #0f6bfe (fixed)
	 * - purple: `--gvt-purp` — `--gvt-purp` #7c20b8 (fixed)
	 */
	color?: TextColor;
	transform?: TextTransform;
};

export const TextMono: ParentComponent<Props> = (props) => {
	const classList = () => ({
		...props.classList,
		[`gvtui-text--${props.size}`]: props.size !== undefined,
		[`gvtui-text--weight-${props.weight}`]: props.weight !== undefined,
		[`gvtui-text--color-${props.color}`]: props.color !== undefined,
		[`gvtui-text--transform-${props.transform}`]: props.transform !== undefined,
	});

	return (
		<Dynamic
			component={props.as ?? "div"}
			class={`gvtui-text-mono ${props.class ?? ""}`}
			classList={classList()}
			style={props.style}
			title={props.title}
		>
			{props.children}
		</Dynamic>
	);
};
